import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useDemoMode } from '@/hooks/useDemoMode';
import { toast } from 'sonner';

export interface UserProfile {
  id: string;
  email: string;
  fullName: string | null;
  role: 'admin' | 'developer' | 'viewer';
  clientProjectId: string | null;
  createdAt: string;
  updatedAt: string;
  lastSignInAt: string | null;
  avatarUrl?: string | null;
}

export function useUsers(filters?: { role?: string; clientProjectId?: string }) {
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  const {
    data: users,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['users', isDemoMode, filters],
    queryFn: async (): Promise<UserProfile[]> => {
      if (isDemoMode) {
        // Return mock user data for demo mode
        return [
          {
            id: 'demo-admin-1',
            email: 'admin@tendencia.ai',
            fullName: 'Demo Admin',
            role: 'admin',
            clientProjectId: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            lastSignInAt: new Date().toISOString(),
          },
          {
            id: 'demo-dev-1',
            email: 'dev@tendencia.ai',
            fullName: 'Demo Developer',
            role: 'developer',
            clientProjectId: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            lastSignInAt: new Date().toISOString(),
          },
          {
            id: 'demo-viewer-1',
            email: 'viewer@example.com',
            fullName: 'Demo Viewer',
            role: 'viewer',
            clientProjectId: 'demo-project-1',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            lastSignInAt: null,
          },
        ];
      }

      try {
        // Build query parameters
        const params = new URLSearchParams();
        if (filters?.role) params.append('role', filters.role);
        if (filters?.clientProjectId) params.append('clientProjectId', filters.clientProjectId);

        const url = `/users${params.toString() ? `?${params}` : ''}`;
        const response = await apiClient.get(url);

        if (response.data?.success && response.data?.data?.users) {
          // Transform backend response to match UserProfile interface
          return response.data.data.users.map((user: any) => ({
            id: user.id,
            email: user.email,
            fullName: user.fullName || user.full_name || user.name || null,
            role: user.role,
            clientProjectId: user.clientProjectId || user.client_project_id || null,
            createdAt: user.createdAt || user.created_at,
            updatedAt: user.updatedAt || user.updated_at,
            lastSignInAt: user.lastSignInAt || user.last_sign_in_at || null,
            avatarUrl: user.avatarUrl || user.avatar_url || null,
          }));
        }
        return [];
      } catch (apiError: any) {
        console.error('Error fetching users:', apiError);
        // If it's a 404 or network error, return empty array
        if (apiError.response?.status === 404 || !apiError.response) {
          return [];
        }
        throw apiError;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const invalidateUsers = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  // Invite user mutation (replaces create user)
  const inviteUser = useMutation({
    mutationFn: async (userData: {
      email: string;
      role: 'admin' | 'developer' | 'viewer';
      fullName?: string;
      avatarUrl?: string;
      clientProjectId?: string;
      password?: string;
    }) => {
      if (isDemoMode) {
        return Promise.resolve({ 
          id: 'demo-new-user', 
          ...userData,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          lastSignInAt: null
        });
      }

      const response = await apiClient.post('/users/invite', userData);
      if (response.data?.success) {
        return response.data.data;
      }
      throw new Error(response.data?.error?.message || 'Error inviting user');
    },
    onSuccess: () => {
      invalidateUsers();
      toast.success(isDemoMode ? 'Usuario simulado exitosamente (Modo Demo)' : 'Usuario invitado exitosamente');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || error.message || 'Error al invitar usuario';
      toast.error(message);
    },
  });

  // Update user mutation
  const updateUser = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; role?: string; fullName?: string; clientProjectId?: string | null; avatarUrl?: string }) => {
      if (isDemoMode) {
        return Promise.resolve({ id, ...updates, updatedAt: new Date().toISOString() });
      }

      const response = await apiClient.put(`/users/${id}`, updates);
      if (response.data?.success) {
        return response.data.data;
      }
      throw new Error(response.data?.error?.message || 'Error updating user');
    },
    onSuccess: () => {
      invalidateUsers();
      toast.success(isDemoMode ? 'Actualización simulada (Modo Demo)' : 'Usuario actualizado exitosamente');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || error.message || 'Error al actualizar usuario';
      toast.error(message);
    },
  });

  // Delete user mutation
  const deleteUser = useMutation({
    mutationFn: async (id: string) => {
      if (isDemoMode) {
        return Promise.resolve(true);
      }

      const response = await apiClient.delete(`/users/${id}`);
      if (response.data?.success) {
        return true;
      }
      throw new Error(response.data?.error?.message || 'Error deleting user');
    },
    onSuccess: () => {
      invalidateUsers();
      toast.success(isDemoMode ? 'Eliminación simulada (Modo Demo)' : 'Usuario eliminado exitosamente');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || error.message || 'Error al eliminar usuario';
      if (message.includes('Cannot delete your own account')) {
        toast.error('No puedes eliminar tu propia cuenta');
      } else {
        toast.error(message);
      }
    },
  });

  return {
    users: users || [],
    isLoading,
    error,
    refetch,
    invalidateUsers,
    inviteUser,
    updateUser,
    deleteUser,
    isUsingMockData: isDemoMode,
  };
}

export function useUser(id: string) {
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: ['user', id, isDemoMode],
    queryFn: async (): Promise<UserProfile | null> => {
      if (isDemoMode) {
        // Return demo user based on ID
        if (id === 'demo-admin-1') {
          return {
            id: 'demo-admin-1',
            email: 'admin@tendencia.ai',
            fullName: 'Demo Admin',
            role: 'admin',
            clientProjectId: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            lastSignInAt: new Date().toISOString(),
          };
        }
        return null;
      }

      try {
        const response = await apiClient.get(`/users/${id}`);
        if (response.data?.success && response.data?.data) {
          const user = response.data.data;
          // Transform backend response to match UserProfile interface
          return {
            id: user.id,
            email: user.email,
            fullName: user.fullName || user.full_name || user.name || null,
            role: user.role,
            clientProjectId: user.clientProjectId || user.client_project_id || null,
            createdAt: user.createdAt || user.created_at,
            updatedAt: user.updatedAt || user.updated_at,
            lastSignInAt: user.lastSignInAt || user.last_sign_in_at || null,
            avatarUrl: user.avatarUrl || user.avatar_url || null,
          };
        }
        return null;
      } catch (apiError: any) {
        console.error('Error fetching user:', apiError);
        if (apiError.response?.status === 404) {
          return null;
        }
        throw apiError;
      }
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}